import { useCallback, useState } from 'react';
import { ITripGroup } from '../types/trip.types';
import { ISidebarState } from '../types/sidebar-state.types';
import { areAllTripsResolved, getNextPendingTripIndex } from '../utils/trip-navigation.utils';
import { resetSidebarState } from '../utils/sidebar-reset.utils';

interface UseAutoNavigationProps {
    tripGroups: ITripGroup[];
    isLoading: boolean;
    navigation: {
        setViewType: (type: 'trip' | 'overall') => void;
        setSelectedTripIndex: (index: number) => void;
        selectedTripIndex: number;
    };
    sidebarState: ISidebarState & {
        openSidebar: (tripGroup: ITripGroup, loadFirstOnly?: boolean) => Promise<void>;
    };
    sidebarController: {
        isSidebarOpen: boolean;
        handleManualClose: () => void;
    };
    setLastOpenedTripId: (id: number | null) => void;
}

export const useAutoNavigation = (props: UseAutoNavigationProps) => {
    const [isNavigating, setIsNavigating] = useState(false);

    const navigateAfterApprovalOrDecline = useCallback(async (currentTripIndex?: number, freshTripGroups?: ITripGroup[]) => {
        const tripGroupsToUse = freshTripGroups || props.tripGroups;

        if (isNavigating) {
            return;
        }
        
        setIsNavigating(true);
        
        try {
            while (props.isLoading) {
                await new Promise(resolve => setTimeout(resolve, 50));
            }
            
            if (freshTripGroups) {
                await new Promise(resolve => setTimeout(resolve, 100));
            }
            
            const finalTripGroups = freshTripGroups || props.tripGroups;
            
            if (finalTripGroups.length > 0 && !props.isLoading) {
                const allResolved = areAllTripsResolved(finalTripGroups);
                
                if (allResolved) {
                    props.navigation.setViewType('overall');
                    props.navigation.setSelectedTripIndex(0);
                    props.setLastOpenedTripId(null);
                    resetSidebarState(props.sidebarState);
                } else {
                    const tripIndex = currentTripIndex ?? props.navigation.selectedTripIndex;
                    const pendingIndex = getNextPendingTripIndex(finalTripGroups, tripIndex);
                    
                    if (pendingIndex !== null && pendingIndex >= 0 && pendingIndex < finalTripGroups.length) {
                        const newTripGroup = finalTripGroups[pendingIndex];
                        
                        if (props.sidebarController.isSidebarOpen) {
                            props.sidebarController.handleManualClose();
                            await new Promise(resolve => setTimeout(resolve, 100));
                        }
                        
                        props.navigation.setSelectedTripIndex(pendingIndex);
                        props.navigation.setViewType('trip');
                        props.setLastOpenedTripId(null);
                        resetSidebarState(props.sidebarState);
                        
                        if (newTripGroup && newTripGroup.lineItems && newTripGroup.lineItems.some(li => li.receiptFileId)) {
                            try {
                                await props.sidebarState.openSidebar(newTripGroup, true);
                            } catch (err) {
                            }
                        }
                    }
                }
            }
        } finally {
            setIsNavigating(false);
        }
    }, [
        props.tripGroups,
        props.isLoading,
        props.navigation,
        props.sidebarState,
        props.sidebarController,
        props.setLastOpenedTripId,
        isNavigating
    ]);

    return {
        navigateAfterApprovalOrDecline,
        isNavigating
    };
};

